import React, { useState, useEffect } from 'react';
import { ChevronDown, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { getApiUrl } from '@/lib/queryClient';
import CONFIG from '@/lib/config';

interface Hub {
  id: string;
  name: string;
  companyName?: string;
}

interface HubSwitcherProps {
  onHubChange: (hubId: string | null) => void;
  selectedHubId: string | null;
}

const HubSwitcher: React.FC<HubSwitcherProps> = ({ onHubChange, selectedHubId }) => {
  const [hubs, setHubs] = useState<Hub[]>(() => {
    const savedHubs = localStorage.getItem('hubs_data');
    return savedHubs ? JSON.parse(savedHubs) : [];
  });
  const [isLoading, setIsLoading] = useState(() => {
    return localStorage.getItem('hubs_loaded') !== 'true';
  });
  const [hasLoaded, setHasLoaded] = useState(() => {
    return localStorage.getItem('hubs_loaded') === 'true';
  });

  useEffect(() => {
    // Check if we need to reset (for debugging)
    const shouldReset = localStorage.getItem('hubs_reset') === 'true';
    if (shouldReset) {
      console.log('🔄 HubSwitcher reset requested');
      localStorage.removeItem('hubs_loaded');
      localStorage.removeItem('hubs_data');
      localStorage.removeItem('hubs_reset');
      setHasLoaded(false);
      setIsLoading(true);
      setHubs([]);
    }
    
    if (hasLoaded) {
      console.log('⏭️  HubSwitcher already loaded, skipping fetch');
      return;
    }
    
    console.log('🔄 HubSwitcher useEffect triggered, current hubs:', hubs.length);
    
    // Check if we have valid cached data
    const savedHubs = localStorage.getItem('hubs_data');
    if (savedHubs) {
      const parsedHubs = JSON.parse(savedHubs);
      console.log('🔄 Found saved hubs in localStorage:', parsedHubs.length);
      if (parsedHubs.length > 0) {
        console.log('🔄 Using cached hubs');
        setHubs(parsedHubs);
        setHasLoaded(true);
        setIsLoading(false);
        return;
      } else {
        console.log('🔄 Clearing empty saved hubs');
        localStorage.removeItem('hubs_data');
        localStorage.removeItem('hubs_loaded');
      }
    }
    
    // Set loading state for fresh fetch
    setIsLoading(true);
    setHubs([]);
    const fetchHubs = async () => {
      try {
        console.log('Fetching hubs from API...');
        // Fetch hubs from the main app's API using session authentication
        console.log('🔑 Using session authentication for Hub Switcher');
        
        const response = await fetch(`${CONFIG.API_BASE_URL}/api/admin/hubs`, {
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });
        console.log('Hub API response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('Hub API data:', data);
          console.log('Hub API results count:', data.hubs?.length || 0);
          
          if (data.hubs && data.hubs.length > 0) {
            const hubList = data.hubs.map((hub: any) => ({
              id: hub.id,
              name: hub.accountName || hub.name,
              companyName: hub.accountName || hub.name
            }));
            console.log('Processed hubs:', hubList);
            setHubs(hubList);
            localStorage.setItem('hubs_data', JSON.stringify(hubList));
          } else {
            console.log('No hubs found in API response');
            // Fallback to mock data
            const mockHubs = [
              { id: 'b.aa65956f-280b-47d2-9e2d-f1d340dee387', name: 'Argent', companyName: 'Argent Ltd' },
              { id: 'b.c4b758c4-188e-47c1-974d-801543288d23', name: 'HG Construction', companyName: 'HG Construction Ltd' },
              { id: 'b.af63439b-572b-4b14-bdf8-b0a9e83dc0e4', name: 'HG Group', companyName: 'HG Group Ltd' }
            ];
            console.log('Using mock hubs:', mockHubs);
            setHubs(mockHubs);
            localStorage.setItem('hubs_data', JSON.stringify(mockHubs));
          }
        } else {
          console.error('Hub API error:', response.status, response.statusText);
          throw new Error(`API error: ${response.status}`);
        }
      } catch (error) {
        console.error('Failed to fetch hubs:', error);
        // Fallback to mock data
        const mockHubs = [
          { id: 'b.aa65956f-280b-47d2-9e2d-f1d340dee387', name: 'Argent', companyName: 'Argent Ltd' },
          { id: 'b.c4b758c4-188e-47c1-974d-801543288d23', name: 'HG Construction', companyName: 'HG Construction Ltd' },
          { id: 'b.af63439b-572b-4b14-bdf8-b0a9e83dc0e4', name: 'HG Group', companyName: 'HG Group Ltd' }
        ];
        console.log('Using mock hubs:', mockHubs);
        setHubs(mockHubs);
        localStorage.setItem('hubs_data', JSON.stringify(mockHubs));
      } finally {
        setIsLoading(false);
        setHasLoaded(true);
        localStorage.setItem('hubs_loaded', 'true');
      }
    };

    fetchHubs();
  }, []);

  const selectedHub = selectedHubId ? hubs.find(hub => hub.id === selectedHubId) : null;

  const handleHubSelect = (hubId: string | null) => {
    onHubChange(hubId);
  };

  console.log('🔄 HubSwitcher render - isLoading:', isLoading, 'hubs count:', hubs.length, 'selectedHubId:', selectedHubId);

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
        <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
      </div>
    );
  }

    return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="h-10 px-4 bg-white border-gray-300 hover:bg-gray-50 shadow-sm">
          <Building2 className="w-4 h-4 mr-2 text-gray-600" />
          <span className="hidden sm:inline font-medium">
            {selectedHub ? selectedHub.name : 'All Hubs'}
          </span>
          <span className="sm:hidden font-medium">
            {selectedHub ? selectedHub.name.substring(0, 10) + '...' : 'All'}
          </span>
          <ChevronDown className="w-4 h-4 ml-2 text-gray-600" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>Select Hub</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        <DropdownMenuItem 
          onClick={() => handleHubSelect(null)}
          className={!selectedHubId ? 'bg-gray-100' : ''}
        >
          <div className="flex items-center justify-between w-full">
            <span>All Hubs</span>
            {!selectedHubId && <Badge variant="secondary" className="text-xs">Active</Badge>}
          </div>
        </DropdownMenuItem>
        
        {hubs.map((hub) => (
          <DropdownMenuItem 
            key={hub.id}
            onClick={() => handleHubSelect(hub.id)}
            className={selectedHubId === hub.id ? 'bg-gray-100' : ''}
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex flex-col">
                <span className="font-medium">{hub.name}</span>
                {hub.companyName && (
                  <span className="text-xs text-gray-500">{hub.companyName}</span>
                )}
              </div>
              {selectedHubId === hub.id && (
                <Badge variant="secondary" className="text-xs">Active</Badge>
              )}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default HubSwitcher;
